import { computed } from 'vue'
import { normalize } from '@/lib/normalize.js'
import { sharedState, ephermeralId } from '@/shared-state.js'
import { SharedStore } from '@/shared-store-core.js'
import schema, { raceEvent, race, participant, newEphemeralObject } from '@/schema.js'
import { authenticationComplete } from '@/lib/authentication.js'
import settings from '@/lib/settings.js'

export { ephermeralId, SharedStore }

class RegistrationSharedStore extends SharedStore {
  #loadingRaceEventsPromise = undefined

  constructor(state) {
    super(state)

    this.computed.activeRaceEvent = computed({
      get: () => this.state.raceEvent[this.state.activeRaceEventId],
      set: re => this.setActiveRaceEvent(re),
    })

    this.computed.activeRace = computed({
      get: () => this.state.race[this.state.activeRaceId],
      set: raceObj => this.setActiveRace(raceObj),
    })
  }

  async loadInitial() {}

  async loadRaceEvents({ requestingSlug, offset, query } = {}) {
    let url = query ? 'race_events/search' : 'race_events'
    let qs = new URLSearchParams()
    if (requestingSlug) qs.append('slug', requestingSlug)
    if (offset) qs.append('o', offset)
    if (query) qs.append('q', query)
    if (qs.toString()) url += '?' + qs.toString()

    if (this.#loadingRaceEventsPromise) return this.#loadingRaceEventsPromise

    this.#loadingRaceEventsPromise = new Promise(async (resolve, reject) => {
      try {
        await authenticationComplete()
        const response = await this.axios.get(url)
        normalize(response.data, this.state, [raceEvent])
        this.honorRequestedActiveRaceEvent()
        resolve(response)
      }
      catch (err) {
        reject(err)
      }
      finally {
        this.#loadingRaceEventsPromise = undefined
      }
    })

    return this.#loadingRaceEventsPromise
  }

  async loadRaceEvent(raceEventId) {
    if (!raceEventId) return undefined

    const id = Number(raceEventId)
    const result = await this.loadEntities({
      url: `/race_events/${id}`,
      entity: raceEvent,
    })

    return this.state.raceEvent?.[id] || result?.response?.data
  }

  async loadRaceRegistration(raceEventId, raceId) {
    await this.setActiveRaceEvent(raceEventId)
    this.setActiveRace(raceId)

    return this.loadRaceEventRegistration(raceEventId)
  }

  async loadRaceEventRegistration(raceEventId) {
    await this.setActiveRaceEvent(raceEventId)

    const raceEventObj = this.state.raceEvent[Number(raceEventId)]
    const races = (raceEventObj?.races || []).filter(Boolean)
    const results = []

    for (const raceObj of races) {
      const result = await this.loadEntities({
        url: `/race_events/${raceEventId}/races/${raceObj.id}/participants`,
        entity: [participant],
        parent: raceObj,
      })
      results.push(result)

      try {
        await this.loadParticipantRegistrationDetails(raceEventId, raceObj.id)
      }
      catch (err) {
        console.warn('Could not load registration participant details', err)
      }
    }

    return results
  }

  async loadParticipantRegistrationDetails(raceEventId, raceId) {
    const fields = [
      'race_id',
      'picked_up_at',
      'shirt_size',
      'notes',
      'registered_at',
    ].join(' ')

    const response = await this.axios.get(
      `/race_events/${raceEventId}/participants/detail`,
      {
        params: {
          race_id: raceId,
          a: fields,
        },
      }
    )

    normalize(response.data, this.state, [participant])
    return response
  }

  async loadParticipantDetail(raceEventId, participantId) {
    const response = await this.axios.get(
      `/race_events/${raceEventId}/participants/${participantId}/detail`
    )
    const existing = this.state.participant[participantId] || { id: participantId }
    normalize({ ...existing, ...response.data }, this.state, participant)
    return this.state.participant[participantId]
  }

  raceEventIdForParticipant(participantObj) {
    return participantObj?.raceEventId ||
      participantObj?.race?.raceEventId ||
      this.state.race?.[participantObj?.raceId]?.raceEventId ||
      this.state.activeRaceEventId
  }

  async saveParticipant(participantObj, payload = participantObj, { raceEventId } = {}) {
    const resolvedRaceEventId = raceEventId || this.raceEventIdForParticipant(participantObj)

    if (!resolvedRaceEventId) {
      throw new Error('RaceEventMissing')
    }

    return this.saveEntity({
      path: `/race_events/${resolvedRaceEventId}/participants`,
      entity: schema.participant,
      object: payload,
      sourceObject: participantObj,
    })
  }

  async setParticipantPickedUp(participantObj, pickedUp, { raceEventId } = {}) {
    raceEventId = raceEventId || this.raceEventIdForParticipant(participantObj)
    const payload = {
      id: participantObj.id,
      pickedUpAt: pickedUp ? new Date() : null,
    }

    return this.saveParticipant(participantObj, payload, { raceEventId })
  }

  newRaceDayParticipant(raceObj = this.getters.activeRace) {
    return newEphemeralObject(participant, this.state, {
      raceId: raceObj?.id,
      raceEventId: raceObj?.raceEventId || this.state.activeRaceEventId,
      pickedUpAt: new Date(),
      registeredAt: new Date(),
    })
  }

  didLoadEntities({ entityName } = {}) {
    if (entityName === raceEvent.key) this.honorRequestedActiveRaceEvent()
  }

  honorRequestedActiveRaceEvent() {
    const requestedId = this.state.requestedActiveRaceEventId
    if (!requestedId) return false

    const raceEventObj = this.state.raceEvent?.[requestedId]
    if (!raceEventObj) return false

    this.state.requestedActiveRaceEventId = null
    this.state.activeRaceEventId = raceEventObj.id
    this.state.activeRaceEventSlug = raceEventObj.slug || null
    settings.activeRaceEventId = raceEventObj.id
    settings.activeRaceEventSlug = raceEventObj.slug || null
    return true
  }

  async setActiveRaceEvent(raceEventOrId) {
    if (raceEventOrId == null) return

    const requestedId = (typeof raceEventOrId === 'object') ?
      Number(raceEventOrId.id) :
      Number(raceEventOrId)

    if (!requestedId) return
    this.state.requestedActiveRaceEventId = requestedId

    if (this.#loadingRaceEventsPromise) await this.#loadingRaceEventsPromise

    if (!this.state.raceEvent[requestedId]) await this.loadRaceEvent(requestedId)
    this.honorRequestedActiveRaceEvent()
    return this.state.raceEvent[requestedId]
  }

  setActiveRace(raceOrId) {
    const raceId = (typeof raceOrId === 'object') ? raceOrId?.id : raceOrId
    this.state.activeRaceId = raceId ? Number(raceId) : null
    return this.state.race[this.state.activeRaceId]
  }
}

export const store = new RegistrationSharedStore(sharedState)
window._sharedStore = store

export default function install(app, options = {}) {
  app.config.globalProperties.sharedStore = store
}
